/**
 * Normalizes a single report period string to "QX YYYY" format.
 * @param {string} period
 * @returns {string}
 */
const normalizeSinglePeriod = (period) => {
  const trimmed = period.trim().toUpperCase();

  if (/^Q[1-4]\s\d{4}$/.test(trimmed)) return trimmed;

  if (/^\d{4}\sQ[1-4]$/.test(trimmed)) {
    const [year, quarter] = trimmed.split(' ');
    return `${quarter} ${year}`;
  }

  return trimmed;
};

/**
 * Compares two "QX YYYY" formatted strings based on year and quarter order.
 * @param {string} a
 * @param {string} b
 * @param {'asc' | 'desc'} yearOrder
 * @param {'asc' | 'desc'} quarterOrder
 * @returns {number}
 */
const compareNormalizedPeriods = (a, b, yearOrder, quarterOrder) => {
  const [qA, yA] = a.split(' ');
  const [qB, yB] = b.split(' ');

  const yearMultiplier = yearOrder === 'asc' ? 1 : -1;
  const quarterMultiplier = quarterOrder === 'asc' ? 1 : -1;

  const yearDiff = yearMultiplier * (parseInt(yA) - parseInt(yB));
  if (yearDiff !== 0) return yearDiff;

  return quarterMultiplier * (parseInt(qA[1]) - parseInt(qB[1]));
};

/**
 * Validates and formats report period strings to "QX YYYY" format and sorts them.
 * @param {string[]} periods
 * @param {'asc' | 'desc'} [yearOrder='desc']
 * @param {'asc' | 'desc'} [quarterOrder='desc']
 * @returns {string[] | null}
 */
export const normalizeReportPeriods = (
  periods,
  yearOrder = 'desc',
  quarterOrder = 'desc'
) => {
  if (periods === null || periods === undefined) return null;
  if (!Array.isArray(periods) || periods.length === 0) return [];

  const normalized = periods.map(normalizeSinglePeriod);

  return normalized.sort((a, b) =>
    compareNormalizedPeriods(a, b, yearOrder, quarterOrder)
  );
};

/**
 * Normalizes and sorts an array of report period objects based on a specified column and sort order.
 * @param {Object[]} periods
 * @param {string} [sortByColumn='reportPeriodDescription']
 * @param {'asc' | 'desc'} [yearOrder='desc']
 * @param {'asc' | 'desc'} [quarterOrder='desc']
 * @returns {Object[] | null}
 */
export const normalizeReportPeriodObjects = (
  periods,
  sortByColumn = 'reportPeriodDescription',
  yearOrder = 'desc',
  quarterOrder = 'desc'
) => {
  if (!Array.isArray(periods)) return periods ?? null;
  if (periods.length === 0) return [];

  const normalized = periods.map((p) => {
    const value = p[sortByColumn];
    const normalizedValue =
      typeof value === 'string' ? normalizeSinglePeriod(value) : value;

    return {
      ...p,
      [sortByColumn]: normalizedValue,
    };
  });

  const yearMultiplier = yearOrder === 'asc' ? 1 : -1;

  return normalized.sort((a, b) => {
    const valA = a[sortByColumn];
    const valB = b[sortByColumn];

    const qxRegex = /^Q[1-4]\s\d{4}$/;
    if (qxRegex.test(valA) && qxRegex.test(valB)) {
      return compareNormalizedPeriods(valA, valB, yearOrder, quarterOrder);
    }

    const dateA = new Date(valA);
    const dateB = new Date(valB);
    const isDate = !isNaN(dateA.getTime()) && !isNaN(dateB.getTime());
    if (isDate) return yearMultiplier * (dateB.getTime() - dateA.getTime());

    const strA = valA?.toString().toUpperCase() || '';
    const strB = valB?.toString().toUpperCase() || '';
    return strA.localeCompare(strB);
  });
};
